/**
 * Tree layout calculation: positions and dimensions for equipment nodes.
 * Uses LAYOUT constants and asset category helpers.
 */

import type { VisualEquipment } from './types';
import type { LayoutNode } from './types';
import { normalizeEquipmentId } from './idUtils';
import {
  LAYOUT,
  TREE_GRID,
  MIN_NODE_GAP,
  TREE_PERF,
  DC_LAYOUT_PANEL,
  getDcLayoutStringBoxGrid
} from './constants';
import { computeSolarDcLayoutTotals } from './solarPlantSizing';
import {
  isGenerationAsset,
  isStringAsset,
  isConversionAsset
} from './assetTypeConfig';
import { buildChildrenByParentId } from './utils/tree-helpers';

const {
  NODE_WIDTH,
  PARENT_NODE_MIN_WIDTH,
  NODE_HEIGHT,
  MODULE_WIDTH,
  MODULE_HEIGHT,
  HORIZONTAL_SPACING,
  MODULE_SPACING,
  MODULE_GRID_PADDING,
  VERTICAL_SPACING,
  PARENT_CHILD_GAP,
  ROOT_SPACING_MULTIPLIER
} = LAYOUT;

const GAP_H = Math.max(HORIZONTAL_SPACING, MIN_NODE_GAP);
const GAP_V = Math.max(VERTICAL_SPACING, MIN_NODE_GAP);
/** Gap between a parent node bottom and its children row - ensures no overlap */
const PARENT_TO_CHILD_GAP = Math.max(GAP_V, PARENT_CHILD_GAP);
/**
 * Estimated card height for compact parent / string rows — must cover UI (name, code, type, assignee line).
 * If this is smaller than the rendered DOM, rows stack too tight and connectors clip through cards.
 */
/** Room for wrapped title + type line + optional assignee snippet */
const COMPACT_PARENT_CARD_HEIGHT = Math.max(NODE_HEIGHT + 36, 96);
/** Max equipment per horizontal row (children under a parent, and root-level siblings) */
export const EQUIPMENT_PER_ROW = 7;
/** Root-level nodes wrap to the next row after this many */
const ROOTS_PER_ROW = EQUIPMENT_PER_ROW;

export function calculateGridDimensions(totalModules: number): { cols: number; rows: number } {
  const cols = Math.ceil(Math.sqrt(totalModules));
  const rows = Math.ceil(totalModules / cols);
  return { cols, rows };
}

export type TreeLayoutParams = {
  visualEquipmentList: VisualEquipment[];
  ghostNodes: VisualEquipment[];
  expandedNodes: Set<string>;
  /** Optional: override width/height per equipment id (e.g. from drag-resize) */
  nodeSizeOverrides?: Record<string, { width: number; height: number }>;
  /** Optional: override x,y per equipment id (move equipment anywhere); collision-free applied */
  nodePositionOverrides?: Record<string, { x: number; y: number }>;
};

const STEP = TREE_GRID.STEP;
/** Inset so nodes align with grid; absolute children ignore parent padding in the tree container */
const CONTENT_INSET = TREE_GRID.PADDING;

function snapToGrid(v: number): number {
  return Math.round(v / STEP) * STEP;
}

function rectsOverlap(
  ax: number, ay: number, aw: number, ah: number,
  bx: number, by: number, bw: number, bh: number
): boolean {
  return ax < bx + bw && ax + aw > bx && ay < by + bh && ay + ah > by;
}

function getNodeRect(n: LayoutNode): { left: number; top: number; right: number; bottom: number } {
  return {
    left: n.x - n.width / 2,
    top: n.y,
    right: n.x + n.width / 2,
    bottom: n.y + n.height
  };
}

/** Resolve overlaps: nudge nodes so none overlap (with MIN_NODE_GAP). Works on all nodes or only those with overrides. */
function ensureNoOverlap(
  layoutNodes: LayoutNode[],
  gap: number,
  onlyNudgeIds?: Set<string>
): void {
  const getRect = (n: LayoutNode) => {
    const r = getNodeRect(n);
    return { ...r, width: r.right - r.left, height: r.bottom - r.top };
  };

  let iterations = 0;
  const maxIterations = layoutNodes.length * layoutNodes.length * 6;
  while (iterations < maxIterations) {
    let hadOverlap = false;
    for (let i = 0; i < layoutNodes.length; i++) {
      const a = layoutNodes[i];
      for (let j = i + 1; j < layoutNodes.length; j++) {
        const b = layoutNodes[j];
        if (onlyNudgeIds && !onlyNudgeIds.has(a.equipment.id) && !onlyNudgeIds.has(b.equipment.id)) continue;
        const ra = getRect(a);
        const rb = getRect(b);
        if (!rectsOverlap(ra.left, ra.top, ra.width, ra.height, rb.left, rb.top, rb.width, rb.height)) continue;

        hadOverlap = true;
        const nudgeRight = ra.right + gap - rb.left;
        const nudgeDown = ra.bottom + gap - rb.top;
        const canNudgeB = !onlyNudgeIds || onlyNudgeIds.has(b.equipment.id);
        const canNudgeA = !onlyNudgeIds || onlyNudgeIds.has(a.equipment.id);

        if (canNudgeB) {
          if (nudgeRight > 0) b.x += nudgeRight;
          else if (nudgeDown > 0) b.y += nudgeDown;
        } else if (canNudgeA) {
          const nudgeLeft = rb.right + gap - ra.left;
          const nudgeUp = rb.bottom + gap - ra.top;
          if (nudgeLeft > 0) a.x -= nudgeLeft;
          else if (nudgeUp > 0) a.y -= nudgeUp;
        }
        break;
      }
      if (hadOverlap) break;
    }
    if (!hadOverlap) break;
    iterations++;
  }
}

export function calculateTreeLayout(params: TreeLayoutParams): LayoutNode[] {
  const {
    visualEquipmentList,
    ghostNodes,
    expandedNodes,
    nodeSizeOverrides = {},
    nodePositionOverrides = {}
  } = params;

  const layoutNodes: LayoutNode[] = [];
  const childrenByParentId = buildChildrenByParentId(visualEquipmentList, ghostNodes);
  const equipmentById = new Map(visualEquipmentList.map(e => [e.id, e]));

  const isModuleEquipment = (eq: VisualEquipment) => {
    if (eq.isGhost) return false;
    if (eq.assetCategory) return eq.assetCategory === 'generation';
    return isGenerationAsset(eq.assetType);
  };

  const isStringEquipment = (eq: VisualEquipment) => {
    if (eq.isGhost) return false;
    if (eq.assetCategory) return eq.assetCategory === 'string';
    return isStringAsset(eq.assetType);
  };

  const isConversionEquipment = (eq: VisualEquipment) => {
    if (eq.isGhost) return false;
    if (eq.assetCategory) return eq.assetCategory === 'conversion';
    return isConversionAsset(eq.assetType);
  };

  const getCombinedChildren = (eqId: string): VisualEquipment[] => {
    const eqN = normalizeEquipmentId(eqId);
    if (eqN == null) return [];
    return childrenByParentId.get(eqN) ?? [];
  };

  /**
   * Compute dimensions for the inline "Solar DC Layout" panel card.
   * Collapsed: compact parent card.
   * Expanded: header + N string rows (module cells wrap at MAX_CELLS_PER_ROW_RENDER) + footer.
   */
  const getDcLayoutPanelDimensions = (equipment: VisualEquipment): { width: number; height: number } => {
    const layout = equipment.solarDcLayout;
    const collapsedHeight = Math.max(
      COMPACT_PARENT_CARD_HEIGHT,
      DC_LAYOUT_PANEL.HEADER_HEIGHT + DC_LAYOUT_PANEL.FOOTER_HEIGHT + 4
    );
    if (!layout) {
      return {
        width: Math.max(NODE_WIDTH, PARENT_NODE_MIN_WIDTH),
        height: collapsedHeight
      };
    }
    if (!expandedNodes.has(equipment.id)) {
      return {
        width: Math.max(NODE_WIDTH, PARENT_NODE_MIN_WIDTH),
        height: collapsedHeight
      };
    }

    const totalStrings = Math.max(0, layout.stringsPerInverter | 0);
    const totals = computeSolarDcLayoutTotals(layout);

    const visibleStrings = Math.min(totalStrings, DC_LAYOUT_PANEL.MAX_STRING_ROWS_RENDER);
    let maxPanelCols = 1;
    let rowsTotalH = 0;
    const cap = DC_LAYOUT_PANEL.MAX_CELLS_PER_ROW_RENDER;
    const frameExtra = DC_LAYOUT_PANEL.STRING_BOX_FRAME_VERTICAL_EXTRA;
    for (let i = 0; i < visibleStrings; i++) {
      const nMods = totals.perStringModules[i] ?? 0;
      const { cols, rows } = getDcLayoutStringBoxGrid(nMods);
      maxPanelCols = Math.max(maxPanelCols, cols);
      const overflow = nMods > cap;
      const h =
        frameExtra +
        2 * DC_LAYOUT_PANEL.ROW_PADDING +
        rows * (DC_LAYOUT_PANEL.MODULE_CELL_HEIGHT + DC_LAYOUT_PANEL.MODULE_CELL_GAP) -
        DC_LAYOUT_PANEL.MODULE_CELL_GAP +
        (overflow ? DC_LAYOUT_PANEL.STRING_BOX_OVERFLOW_LINE_HEIGHT : 0);
      rowsTotalH += h;
    }
    if (visibleStrings > 1) {
      rowsTotalH += (visibleStrings - 1) * DC_LAYOUT_PANEL.STRING_ROW_GAP;
    }

    const gridContentW =
      maxPanelCols > 0
        ? maxPanelCols * DC_LAYOUT_PANEL.MODULE_CELL_WIDTH +
          (maxPanelCols - 1) * DC_LAYOUT_PANEL.MODULE_CELL_GAP
        : 0;
    const gridFrameW = gridContentW + 2 * DC_LAYOUT_PANEL.ROW_PADDING;

    const panelInnerW = DC_LAYOUT_PANEL.STRING_LABEL_WIDTH + 8 + gridFrameW;
    const panelOuterW = Math.min(
      DC_LAYOUT_PANEL.MAX_PANEL_WIDTH,
      Math.max(DC_LAYOUT_PANEL.MIN_PANEL_WIDTH, panelInnerW + 2 * 14)
    );

    const truncatedBanner = totalStrings > visibleStrings ? 22 : 0;

    const panelOuterH =
      DC_LAYOUT_PANEL.HEADER_HEIGHT +
      DC_LAYOUT_PANEL.PANEL_BODY_PADDING +
      rowsTotalH +
      DC_LAYOUT_PANEL.PANEL_BODY_PADDING +
      truncatedBanner +
      DC_LAYOUT_PANEL.FOOTER_HEIGHT;

    return {
      width: Math.max(NODE_WIDTH, panelOuterW),
      height: Math.max(collapsedHeight, panelOuterH)
    };
  };

  const getNodeDimensions = (equipmentId: string, isGhost: boolean = false): { width: number; height: number } => {
    const override = nodeSizeOverrides[equipmentId];
    if (override) return override;

    if (isGhost) return { width: NODE_WIDTH, height: 48 };

    const equipment = equipmentById.get(equipmentId);
    if (!equipment) return { width: NODE_WIDTH, height: NODE_HEIGHT };

    // Solar DC Layout inline summary panel (mega layout only; no per-string virtual children)
    if (equipment.isDcLayoutPanelHost) {
      return getDcLayoutPanelDimensions(equipment);
    }

    const children = getCombinedChildren(equipmentId);
    const lazyN = equipment?.lazyChildCount ?? 0;
    const isStringWithModules =
      isStringEquipment(equipment) &&
      children.some(child => !child.isGhost && isModuleEquipment(child));

    if (isStringWithModules) {
      // Collapsed: compact card only (matches hidden subtree in positionNode)
      if (!expandedNodes.has(equipment.id)) {
        return {
          width: Math.max(NODE_WIDTH, PARENT_NODE_MIN_WIDTH),
          height: COMPACT_PARENT_CARD_HEIGHT
        };
      }
      const moduleChildren = children.filter(child => !child.isGhost && isModuleEquipment(child));
      const gridCellCount = Math.min(moduleChildren.length, TREE_PERF.MAX_MODULE_GRID_CELLS);
      const { cols, rows } = calculateGridDimensions(gridCellCount);
      const gridWidth = cols * MODULE_WIDTH + (cols - 1) * MODULE_SPACING;
      const gridHeight = rows * MODULE_HEIGHT + (rows - 1) * MODULE_SPACING;
      const containerPadding = 4;
      const headerHeight = 32;
      const footerHeight = 36;
      const truncatedBanner =
        moduleChildren.length > TREE_PERF.MAX_MODULE_GRID_CELLS ? 22 : 0;
      const gridWithFrameW = gridWidth + 2 * MODULE_GRID_PADDING;
      const gridWithFrameH = gridHeight + 2 * MODULE_GRID_PADDING;
      return {
        width: Math.max(NODE_WIDTH, gridWithFrameW + containerPadding * 2),
        height:
          containerPadding * 2 +
          headerHeight +
          gridWithFrameH +
          footerHeight +
          truncatedBanner
      };
    }
    // Only use tiny module cell dimensions if the equipment is actually nested inside a String.
    // Root-level generation assets (e.g. Solar PV, Solar Energy Meter added without a parent)
    // must render as full cards, not as 18×20px module cells.
    if (isModuleEquipment(equipment)) {
      const parent = equipment.parentEquipmentId
        ? equipmentById.get(equipment.parentEquipmentId)
        : null;
      const parentIsString = parent ? isStringEquipment(parent) : false;
      if (parentIsString) {
        return { width: MODULE_WIDTH, height: MODULE_HEIGHT };
      }
      // No string parent → treat as a normal card
      return { width: NODE_WIDTH, height: NODE_HEIGHT };
    }
    // Parent nodes (have children or unloaded DB children): wider so name + "X children" show
    if (children.length > 0 || lazyN > 0) {
      return { width: Math.max(NODE_WIDTH, PARENT_NODE_MIN_WIDTH), height: COMPACT_PARENT_CARD_HEIGHT };
    }
    return { width: NODE_WIDTH, height: NODE_HEIGHT };
  };

  const getSubtreeWidth = (equipmentId: string, isGhost: boolean = false): number => {
    const selfDims = getNodeDimensions(equipmentId, isGhost);
    if (isGhost) return selfDims.width;

    const equipment = equipmentById.get(equipmentId);
    if (!equipment) return selfDims.width;

    const children = getCombinedChildren(equipmentId);
    if (children.length === 0) return selfDims.width;

    // Collapsed: reserve only this node's width (layout matches positionNode — no hidden children)
    if (!expandedNodes.has(equipment.id)) return selfDims.width;

    const isStringWithModules =
      isStringEquipment(equipment) &&
      children.some(child => !child.isGhost && isModuleEquipment(child));

    if (isStringWithModules) {
      const nonModuleChildren = children.filter(c => !(!c.isGhost && isModuleEquipment(c)));
      if (nonModuleChildren.length === 0) return selfDims.width;
      const childrenWidths = nonModuleChildren.map(child => getSubtreeWidth(child.id, !!child.isGhost));
      const totalChildrenWidth = childrenWidths.reduce((sum, w) => sum + w + GAP_H, -GAP_H);
      return Math.max(selfDims.width, totalChildrenWidth);
    }

    const childrenWidths = children.map(child => getSubtreeWidth(child.id, !!child.isGhost));
    const totalChildrenWidth: number =
      children.length > EQUIPMENT_PER_ROW
        ? (() => {
            let maxRowWidth = 0;
            for (let r = 0; r < children.length; r += EQUIPMENT_PER_ROW) {
              const rowWidths = childrenWidths.slice(r, r + EQUIPMENT_PER_ROW);
              const rowWidth = rowWidths.reduce((sum, w) => sum + w + GAP_H, -GAP_H);
              maxRowWidth = Math.max(maxRowWidth, rowWidth);
            }
            return maxRowWidth;
          })()
        : childrenWidths.reduce((sum, w) => sum + w + GAP_H, -GAP_H);
    return Math.max(selfDims.width, totalChildrenWidth);
  };

  /**
   * Total vertical span from this node's top through its expanded subtree (matches row wrap at EQUIPMENT_PER_ROW).
   * Used to stack root rows without overlapping.
   */
  const getSubtreeHeight = (equipmentId: string, isGhost: boolean = false): number => {
    const dims = getNodeDimensions(equipmentId, isGhost);
    if (isGhost) return dims.height;

    const equipment = equipmentById.get(equipmentId);
    if (!equipment) return dims.height;
    if (!expandedNodes.has(equipment.id)) return dims.height;

    const children = getCombinedChildren(equipment.id);
    if (children.length === 0) return dims.height;

    const isStringWithModules =
      isStringEquipment(equipment) &&
      children.some(child => !child.isGhost && isModuleEquipment(child));

    const childrenToPosition = isStringWithModules
      ? children.filter(c => !(!c.isGhost && isModuleEquipment(c)))
      : children;

    if (childrenToPosition.length === 0) return dims.height;

    const wrapAt =
      childrenToPosition.length > EQUIPMENT_PER_ROW ? EQUIPMENT_PER_ROW : childrenToPosition.length;
    let total = dims.height + PARENT_TO_CHILD_GAP;
    for (let r = 0; r < childrenToPosition.length; r += wrapAt) {
      const rowChildren = childrenToPosition.slice(r, r + wrapAt);
      const maxRowSpan = Math.max(
        ...rowChildren.map(c => getSubtreeHeight(c.id, !!c.isGhost))
      );
      total += maxRowSpan + GAP_V;
    }
    return total;
  };

  const positionNode = (element: VisualEquipment, x: number, y: number, level: number) => {
    const dims = getNodeDimensions(element.id ?? '', !!element.isGhost);
    layoutNodes.push({
      equipment: element,
      x,
      y,
      width: dims.width,
      height: dims.height,
      level
    });

    if (element.isGhost) return;
    if (!expandedNodes.has(element.id)) return;

    const children = getCombinedChildren(element.id);
    if (children.length > 0) {
      const isStringWithModules =
        element.assetType.toLowerCase().includes('string') &&
        children.some(c => !c.isGhost && isModuleEquipment(c));

      const childrenToPosition = isStringWithModules
        ? children.filter(c => !(!c.isGhost && isModuleEquipment(c)))
        : children;

      if (childrenToPosition.length > 0) {
        const childrenWidths = childrenToPosition.map(c => getSubtreeWidth(c.id, !!c.isGhost));
        const wrapAt =
          childrenToPosition.length > EQUIPMENT_PER_ROW ? EQUIPMENT_PER_ROW : childrenToPosition.length;
        const rows: Array<{ start: number; count: number }> = [];
        for (let r = 0; r < childrenToPosition.length; r += wrapAt) {
          rows.push({ start: r, count: Math.min(wrapAt, childrenToPosition.length - r) });
        }
        let currentChildY = y + dims.height + PARENT_TO_CHILD_GAP;
        rows.forEach((row) => {
          const rowChildren = childrenToPosition.slice(row.start, row.start + row.count);
          const rowWidths = childrenWidths.slice(row.start, row.start + row.count);
          const rowTotalWidth = rowWidths.reduce((sum, w) => sum + w + GAP_H, -GAP_H);
          let currentX = x - rowTotalWidth / 2;
          /** Full vertical span per column (card + expanded descendants), not card height only */
          let maxRowSubtreeSpan = 0;
          rowChildren.forEach((child, i) => {
            const w = rowWidths[i];
            positionNode(child, currentX + w / 2, currentChildY, level + 1);
            currentX += w + GAP_H;
            maxRowSubtreeSpan = Math.max(
              maxRowSubtreeSpan,
              getSubtreeHeight(child.id, !!child.isGhost)
            );
          });
          currentChildY += maxRowSubtreeSpan + GAP_V;
        });
      }
    }
  };

  const rootEquipment = visualEquipmentList.filter(e => !normalizeEquipmentId(e.parentEquipmentId));
  const rootGhosts = ghostNodes.filter(g => !g.parentId);
  const allRoots = [...rootEquipment, ...rootGhosts];

  let currentX = 0;
  let currentY = 40;
  let rootsInRow = 0;
  let rowMaxSubtreeHeight = 0;
  const rootSpacing = Math.max(HORIZONTAL_SPACING * ROOT_SPACING_MULTIPLIER, MIN_NODE_GAP);
  /** Extra gap between stacked root rows */
  const rootRowGap = GAP_V * 2;

  allRoots.forEach(root => {
    const width = getSubtreeWidth(root.id, !!root.isGhost);
    const subtreeH = getSubtreeHeight(root.id, !!root.isGhost);

    if (rootsInRow >= ROOTS_PER_ROW) {
      currentY += rowMaxSubtreeHeight + rootRowGap;
      currentX = 0;
      rootsInRow = 0;
      rowMaxSubtreeHeight = 0;
    }

    positionNode(root, currentX + width / 2, currentY, 0);
    currentX += width + rootSpacing;
    rootsInRow += 1;
    rowMaxSubtreeHeight = Math.max(rowMaxSubtreeHeight, subtreeH);
  });

  const overlapCap = TREE_PERF.OVERLAP_RESOLVE_MAX_NODES;
  const hasOverrides = Object.keys(nodePositionOverrides).length > 0;

  if (hasOverrides) {
    for (const node of layoutNodes) {
      const override = nodePositionOverrides[node.equipment.id];
      if (override) {
        node.x = snapToGrid(override.x);
        node.y = snapToGrid(override.y);
      }
    }
    if (layoutNodes.length <= overlapCap) {
      ensureNoOverlap(layoutNodes, MIN_NODE_GAP, new Set(Object.keys(nodePositionOverrides)));
    }
  }

  if (layoutNodes.length <= overlapCap) {
    ensureNoOverlap(layoutNodes, MIN_NODE_GAP);
  }

  // Absolute-positioned tree nodes ignore the scroll container's padding; nudge so left/top
  // match TREE_GRID.PADDING (same inset as the background grid).
  if (layoutNodes.length > 0) {
    let minLeft = Infinity;
    let minTop = Infinity;
    for (const n of layoutNodes) {
      minLeft = Math.min(minLeft, n.x - n.width / 2);
      minTop = Math.min(minTop, n.y);
    }
    const shiftX = minLeft < CONTENT_INSET ? CONTENT_INSET - minLeft : 0;
    const shiftY = minTop < CONTENT_INSET ? CONTENT_INSET - minTop : 0;
    if (shiftX !== 0 || shiftY !== 0) {
      for (const n of layoutNodes) {
        n.x += shiftX;
        n.y += shiftY;
      }
    }
  }

  return layoutNodes;
}

export { LAYOUT };
